import { useState, useRef, useCallback } from 'react';
import Modal from '../../components/ui/Modal';
import Button from '../../components/ui/Button';
import Notice from '../../components/ui/Notice';
import { bulkImportReportingLines } from '../../hooks/useFirestore';
import { useToast } from '../../contexts/ToastContext';
import { cn } from '../../utils/cn';
import { isEmail, normalizeEmail } from '../../utils/hierarchy';

/* ── CSV column → reporting line field mapping ── */
const FIELD_MAP = {
  employeeemail:    'employeeEmail',
  employee_email:   'employeeEmail',
  email:            'employeeEmail',
  employeename:     'employeeName',
  employee_name:    'employeeName',
  name:             'employeeName',
  supervisoremail:  'supervisorEmail',
  supervisor_email: 'supervisorEmail',
  manageremail:     'supervisorEmail',
  manager_email:    'supervisorEmail',
  supervisorname:   'supervisorName',
  supervisor_name:  'supervisorName',
  managername:      'supervisorName',
  manager_name:     'supervisorName',
};

const CSV_TEMPLATE_HEADERS = [
  'Employee Email',
  'Employee Name',
  'Supervisor Email',
  'Supervisor Name',
];

/* ── Helpers ── */
function parseCsvLine(line) {
  const result = [];
  let current = '';
  let inQuotes = false;
  for (let i = 0; i < line.length; i++) {
    const ch = line[i];
    if (inQuotes) {
      if (ch === '"' && line[i + 1] === '"') { current += '"'; i++; }
      else if (ch === '"') { inQuotes = false; }
      else { current += ch; }
    } else {
      if (ch === '"') { inQuotes = true; }
      else if (ch === ',') { result.push(current.trim()); current = ''; }
      else { current += ch; }
    }
  }
  result.push(current.trim());
  return result;
}

function parseCsv(text) {
  const lines = text.split(/\r?\n/).filter((l) => l.trim());
  if (lines.length < 2) return { headers: [], rows: [] };
  const headers = parseCsvLine(lines[0]);
  const rows = lines.slice(1).map((line) => {
    const vals = parseCsvLine(line);
    const obj = {};
    headers.forEach((h, i) => { obj[h] = vals[i] || ''; });
    return obj;
  });
  return { headers, rows };
}

function mapRow(raw) {
  const mapped = {};
  for (const [csvCol, value] of Object.entries(raw)) {
    const key = FIELD_MAP[csvCol.toLowerCase().replace(/[\s-]/g, '')];
    if (key) mapped[key] = value;
  }
  let { employeeEmail, employeeName, supervisorEmail, supervisorName } = mapped;
  // HR exports have arrived with the name and email columns swapped.
  const employeeSwapped = !isEmail(employeeEmail) && isEmail(employeeName);
  const supervisorSwapped = !isEmail(supervisorEmail) && isEmail(supervisorName);
  if (employeeSwapped) [employeeEmail, employeeName] = [employeeName, employeeEmail];
  if (supervisorSwapped) [supervisorEmail, supervisorName] = [supervisorName, supervisorEmail];
  return {
    employeeEmail: normalizeEmail(employeeEmail),
    employeeName: (employeeName || '').trim(),
    supervisorEmail: normalizeEmail(supervisorEmail),
    supervisorName: (supervisorName || '').trim(),
    swapped: employeeSwapped || supervisorSwapped,
  };
}

function downloadTemplate() {
  const csv = CSV_TEMPLATE_HEADERS.join(',') + '\n'
    + 'sarah.johnson@company.com,Sarah Johnson,matthew.kim@company.com,Matthew Kim\n'
    + 'david.chen@company.com,David Chen,matthew.kim@company.com,Matthew Kim\n'
    + 'alice.wong@company.com,Alice Wong,sarah.johnson@company.com,Sarah Johnson\n'
    + 'bob.smith@company.com,Bob Smith,david.chen@company.com,David Chen\n'
    + 'matthew.kim@company.com,Matthew Kim,matthew.kim@company.com,Matthew Kim\n';
  const blob = new Blob([csv], { type: 'text/csv' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = 'reporting_structure_template.csv';
  a.click();
  URL.revokeObjectURL(url);
}

/* ── Component ── */
export default function ReportingImportModal({ open, onClose, cycleId, communicatorEmails = [] }) {
  const toast = useToast();
  const fileRef = useRef(null);
  const [dragOver, setDragOver] = useState(false);
  const [file, setFile] = useState(null);
  const [preview, setPreview] = useState(null);
  const [importing, setImporting] = useState(false);
  const [result, setResult] = useState(null);

  const reset = useCallback(() => {
    setFile(null);
    setPreview(null);
    setResult(null);
  }, []);

  const handleClose = useCallback(() => {
    reset();
    onClose();
  }, [reset, onClose]);

  const processFile = useCallback((f) => {
    if (!f || !f.name.endsWith('.csv')) {
      toast.error('Please upload a .csv file');
      return;
    }
    setFile(f);
    const reader = new FileReader();
    reader.onload = (e) => {
      const { headers, rows } = parseCsv(e.target.result);
      if (rows.length === 0) {
        toast.error('CSV is empty or has no data rows');
        return;
      }
      const mapped = rows.map(mapRow);
      const valid = mapped.filter((r) => isEmail(r.employeeEmail) && isEmail(r.supervisorEmail));
      const invalid = mapped.length - valid.length;

      // Build tree stats for preview
      const supervisors = new Set(valid.map((r) => r.supervisorEmail));
      const roots = valid.filter((r) => r.employeeEmail === r.supervisorEmail);

      setPreview({
        headers,
        rows: valid,
        totalParsed: mapped.length,
        invalidCount: invalid,
        swappedCount: valid.filter((r) => r.swapped).length,
        uniqueSupervisors: supervisors.size,
        rootCount: roots.length,
        people: new Set(valid.flatMap((r) => [r.employeeEmail, r.supervisorEmail])),
      });
    };
    reader.readAsText(f);
  }, [toast]);

  // Computed on render: the cycle's compensations may still be loading when the file is picked.
  const communicators = [...new Set(communicatorEmails.map(normalizeEmail).filter(Boolean))];
  const coverage = preview && {
    total: communicators.length,
    matched: communicators.filter((e) => preview.people.has(e)).length,
  };

  const handleDrop = useCallback((e) => {
    e.preventDefault();
    setDragOver(false);
    const f = e.dataTransfer.files?.[0];
    processFile(f);
  }, [processFile]);

  const handleImport = async () => {
    if (!preview?.rows?.length) return;
    setImporting(true);
    try {
      const res = await bulkImportReportingLines(cycleId, preview.rows);
      setResult(res);
      if (res.errors.length === 0) {
        toast.success(`Imported ${res.imported} reporting lines`);
      } else {
        toast.error(`Import completed with ${res.errors.length} error(s)`);
      }
    } catch (e) {
      console.error(e);
      toast.error('Import failed: ' + e.message);
    }
    setImporting(false);
  };

  return (
    <Modal
      open={open}
      onClose={handleClose}
      title="Import Reporting Structure"
      size="xl"
      footer={
        <div className="flex items-center justify-between">
          <button onClick={downloadTemplate} className="text-sm text-accent-600 hover:text-accent-700 font-medium flex items-center gap-1.5">
            <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}><path strokeLinecap="round" strokeLinejoin="round" d="M3 16.5v2.25A2.25 2.25 0 005.25 21h13.5A2.25 2.25 0 0021 18.75V16.5M16.5 12L12 16.5m0 0L7.5 12m4.5 4.5V3" /></svg>
            Download Template
          </button>
          <div className="flex gap-3">
            {result ? (
              <Button onClick={handleClose}>Done</Button>
            ) : (
              <>
                <Button variant="secondary" onClick={handleClose}>Cancel</Button>
                <Button onClick={handleImport} loading={importing} disabled={!preview?.rows?.length}>
                  Import {preview?.rows?.length ? `${preview.rows.length} Line${preview.rows.length === 1 ? '' : 's'}` : ''}
                </Button>
              </>
            )}
          </div>
        </div>
      }
    >
      <div className="space-y-6">
        {/* Info banner */}
        <div className="bg-blue-50 rounded-xl border border-blue-200 p-4 flex items-start gap-3">
          <svg className="w-5 h-5 text-blue-500 mt-0.5 shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}><path strokeLinecap="round" strokeLinejoin="round" d="M11.25 11.25l.041-.02a.75.75 0 011.063.852l-.708 2.836a.75.75 0 001.063.853l.041-.021M21 12a9 9 0 11-18 0 9 9 0 0118 0zm-9-3.75h.008v.008H12V8.25z" /></svg>
          <div className="text-xs text-blue-700">
            <p className="font-semibold mb-1">CSV Format</p>
            <p>Each row maps an employee to their supervisor. Root nodes (e.g. CEO) should list themselves as their own supervisor. Columns: <span className="font-mono bg-blue-100 px-1 rounded">Employee Email</span>, <span className="font-mono bg-blue-100 px-1 rounded">Employee Name</span>, <span className="font-mono bg-blue-100 px-1 rounded">Supervisor Email</span>, <span className="font-mono bg-blue-100 px-1 rounded">Supervisor Name</span>.</p>
          </div>
        </div>

        {/* Upload zone */}
        {!preview && !result && (
          <div
            className={cn(
              'border-2 border-dashed rounded-2xl p-10 text-center transition-colors cursor-pointer',
              dragOver ? 'border-accent-500 bg-accent-50' : 'border-surface-300 hover:border-accent-400 hover:bg-surface-50',
            )}
            onDragOver={(e) => { e.preventDefault(); setDragOver(true); }}
            onDragLeave={() => setDragOver(false)}
            onDrop={handleDrop}
            onClick={() => fileRef.current?.click()}
          >
            <input
              ref={fileRef}
              type="file"
              accept=".csv"
              className="hidden"
              onChange={(e) => processFile(e.target.files?.[0])}
            />
            <div className="flex flex-col items-center gap-3">
              <div className="w-14 h-14 rounded-2xl bg-accent-100 flex items-center justify-center">
                <svg className="w-7 h-7 text-accent-600" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}>
                  <path strokeLinecap="round" strokeLinejoin="round" d="M3 16.5v2.25A2.25 2.25 0 005.25 21h13.5A2.25 2.25 0 0021 18.75V16.5m-13.5-9L12 3m0 0l4.5 4.5M12 3v13.5" />
                </svg>
              </div>
              <div>
                <p className="text-sm font-semibold text-navy-900">Drop a CSV file here or click to browse</p>
                <p className="text-xs text-navy-400 mt-1">Supports Employee Email, Employee Name, Supervisor Email, Supervisor Name</p>
              </div>
            </div>
          </div>
        )}

        {/* Preview */}
        {preview && !result && (
          <div className="space-y-4">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-xl bg-emerald-100 flex items-center justify-center">
                <svg className="w-5 h-5 text-emerald-600" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}><path strokeLinecap="round" strokeLinejoin="round" d="M4.5 12.75l6 6 9-13.5" /></svg>
              </div>
              <div>
                <p className="text-sm font-semibold text-navy-900">
                  {file?.name} — {preview.rows.length} valid row{preview.rows.length === 1 ? '' : 's'}
                </p>
                {preview.invalidCount > 0 && (
                  <p className="text-xs text-amber-600">{preview.invalidCount} row(s) skipped (missing or invalid employee or supervisor email)</p>
                )}
              </div>
              <Button variant="ghost" size="sm" onClick={reset} className="ml-auto">Change File</Button>
            </div>

            {preview.swappedCount > 0 && (
              <Notice tone="info" title={`Fixed ${preview.swappedCount} row${preview.swappedCount === 1 ? '' : 's'} with name and email swapped`}>
                The email columns held names and the name columns held emails, so they were switched back. Check the preview below.
              </Notice>
            )}
            {coverage.total === 0 ? (
              <Notice tone="info" title="No compensations in this cycle yet">
                Manager rollups are built automatically once compensations are imported too, in either order.
              </Notice>
            ) : coverage.matched === 0 ? (
              <Notice tone="danger" title={`None of this cycle's ${coverage.total} communicators are in this file`}>
                Managers won't see anyone in My Team. Check that this is the right file before importing.
              </Notice>
            ) : coverage.matched < coverage.total ? (
              <Notice tone="warning" title={`${coverage.matched} of ${coverage.total} communicators are in this file`}>
                The managers of the other {coverage.total - coverage.matched} won't see them in My Team.
              </Notice>
            ) : (
              <Notice tone="success" title={`All ${coverage.total} communicators in this cycle are in this file`} />
            )}

            {/* Quick stats */}
            <div className="grid grid-cols-3 gap-3">
              <div className="bg-surface-50 rounded-xl border border-surface-200 p-3 text-center">
                <p className="text-lg font-bold text-navy-900">{preview.rows.length}</p>
                <p className="text-[11px] font-semibold text-navy-400 uppercase tracking-wider">Employees</p>
              </div>
              <div className="bg-surface-50 rounded-xl border border-surface-200 p-3 text-center">
                <p className="text-lg font-bold text-navy-900">{preview.uniqueSupervisors}</p>
                <p className="text-[11px] font-semibold text-navy-400 uppercase tracking-wider">Supervisors</p>
              </div>
              <div className="bg-surface-50 rounded-xl border border-surface-200 p-3 text-center">
                <p className="text-lg font-bold text-navy-900">{preview.rootCount}</p>
                <p className="text-[11px] font-semibold text-navy-400 uppercase tracking-wider">Root Nodes</p>
              </div>
            </div>

            {/* Table preview (first 10 rows) */}
            <div className="overflow-x-auto rounded-xl border border-surface-200">
              <table className="min-w-full text-xs">
                <thead>
                  <tr className="bg-surface-50 border-b border-surface-200">
                    <th className="px-3 py-2 text-left font-semibold text-navy-500">Employee</th>
                    <th className="px-3 py-2 text-left font-semibold text-navy-500">Email</th>
                    <th className="px-3 py-2 text-left font-semibold text-navy-500">Supervisor</th>
                    <th className="px-3 py-2 text-left font-semibold text-navy-500">Supervisor Email</th>
                  </tr>
                </thead>
                <tbody>
                  {preview.rows.slice(0, 10).map((row, i) => (
                    <tr key={i} className={cn(
                      'border-b border-surface-100 last:border-0',
                      row.employeeEmail === row.supervisorEmail && 'bg-amber-50/50',
                    )}>
                      <td className="px-3 py-2 text-navy-900 font-medium">{row.employeeName}</td>
                      <td className="px-3 py-2 text-navy-500">{row.employeeEmail}</td>
                      <td className="px-3 py-2 text-navy-900 font-medium">{row.supervisorName}</td>
                      <td className="px-3 py-2 text-navy-500">{row.supervisorEmail}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
              {preview.rows.length > 10 && (
                <div className="px-3 py-2 bg-surface-50 text-xs text-navy-400 text-center">
                  + {preview.rows.length - 10} more rows
                </div>
              )}
            </div>

            <p className="text-xs text-navy-400">
              <span className="inline-block w-3 h-3 rounded bg-amber-50 border border-amber-200 mr-1.5 align-middle"></span>
              Highlighted rows are root nodes (employee = supervisor). Importing will <span className="font-semibold text-navy-600">replace</span> any existing reporting structure for this cycle.
            </p>
          </div>
        )}

        {/* Result */}
        {result && (
          <div className="space-y-4">
            <div className="bg-emerald-50 rounded-xl border border-emerald-200 p-6 text-center">
              <div className="w-12 h-12 mx-auto rounded-full bg-emerald-100 flex items-center justify-center mb-3">
                <svg className="w-6 h-6 text-emerald-600" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}><path strokeLinecap="round" strokeLinejoin="round" d="M4.5 12.75l6 6 9-13.5" /></svg>
              </div>
              <p className="text-2xl font-bold text-emerald-700">{result.imported}</p>
              <p className="text-sm font-semibold text-emerald-600 mt-1">Reporting lines imported</p>
            </div>
            {result.errors.length > 0 && (
              <div className="bg-red-50 rounded-xl border border-red-200 p-4">
                <p className="text-sm font-semibold text-red-700 mb-2">{result.errors.length} Error(s)</p>
                <ul className="text-xs text-red-600 space-y-1">
                  {result.errors.map((err, i) => (
                    <li key={i}>{err.row}: {err.error}</li>
                  ))}
                </ul>
              </div>
            )}
          </div>
        )}
      </div>
    </Modal>
  );
}
